import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListStories, adminPatchStory, adminDeleteStory } from "../../lib/api";
import { Eye, EyeOff, Trash2 } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StoryAdminRow({ s, onToggle, onDelete }) {
  return (
    <div
      data-testid={`admin-story-row-${s.id}`}
      className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start"
    >
      <div className="md:col-span-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs uppercase tracking-[0.18em] text-ember font-accent font-semibold">
            {s.role || "Anonymous"}
          </span>
          {s.published ? (
            <span className="inline-flex items-center gap-1 text-xs font-accent text-river">
              <Eye size={12} strokeWidth={1.5} />
              Published
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-accent text-ink-muted">
              <EyeOff size={12} strokeWidth={1.5} />
              Draft
            </span>
          )}
          {!s.publish_consent && (
            <span className="text-xs font-accent text-destructive">
              (no publish consent)
            </span>
          )}
        </div>
        <div className="font-serif text-xl md:text-2xl text-ink tracking-tight leading-tight">
          {s.title}
        </div>
        <p className="mt-2 text-sm text-ink-muted line-clamp-3 whitespace-pre-wrap">
          {s.story}
        </p>
        <div className="mt-2 text-xs text-ink-muted font-accent">
          — {s.pen_name || "Anonymous"} · {formatDate(s.created_at)}
        </div>
      </div>
      <div className="md:col-span-4 flex md:justify-end gap-2 flex-wrap">
        {s.published ? (
          <button
            onClick={() => onToggle(s, false)}
            data-testid={`admin-unpublish-story-${s.id}-button`}
            className="border border-rule text-ink hover:bg-linen-alt px-3 py-2 text-xs font-accent font-semibold"
          >
            Unpublish
          </button>
        ) : (
          <button
            onClick={() => onToggle(s, true)}
            disabled={!s.publish_consent}
            title={s.publish_consent ? "Publish" : "Submitter did not consent to publication"}
            data-testid={`admin-publish-story-${s.id}-button`}
            className="bg-river text-linen px-3 py-2 text-xs font-accent font-semibold hover:bg-river-dark disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Publish
          </button>
        )}
        <button
          onClick={() => onDelete(s)}
          data-testid={`admin-delete-story-${s.id}-button`}
          className="inline-flex items-center gap-1 border border-rule text-ink-muted hover:border-destructive hover:text-destructive px-3 py-2 text-xs font-accent"
          aria-label="Delete"
        >
          <Trash2 size={14} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

export default function AdminStoriesPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminListStories()
      .then(setStories)
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onToggle = async (s, published) => {
    try {
      await adminPatchStory(s.id, { published });
      toast.success(published ? "Story published." : "Story unpublished.");
      load();
    } catch (e) {
      toast.error("Couldn't update the story.");
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete this story from ${s.pen_name || "Anonymous"}?`)) return;
    try {
      await adminDeleteStory(s.id);
      toast.success("Story deleted.");
      load();
    } catch (e) {
      toast.error("Couldn't delete the story.");
    }
  };

  if (loading) return <p className="text-ink-muted font-accent">Loading stories…</p>;

  return (
    <div data-testid="admin-stories-page">
      <div className="text-sm font-accent text-ink-muted mb-8">
        {stories.length} stor{stories.length === 1 ? "y" : "ies"}
      </div>
      {stories.length === 0 ? (
        <div className="border border-rule bg-linen-alt p-10 text-center text-ink-muted">
          No stories submitted yet.
        </div>
      ) : (
        <div className="divide-y divide-rule border-y border-rule">
          {stories.map((s) => (
            <StoryAdminRow key={s.id} s={s} onToggle={onToggle} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
